import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";

import { Separator } from "@/components/ui/separator";
import {
	conversationStatsQueryOptions,
	modelSummaryQueryOptions,
	tokenUsageQueryOptions,
} from "@/hooks/query/analytics";
import { noIndexSeo } from "@/lib/seo";
import * as m from "@/paraglide/messages";

import { ActivityChart } from "./-components/activity-chart";
import { CostBreakdown } from "./-components/cost-breakdown";
import { StatCards } from "./-components/stat-cards";
import { TimeRangeSelect } from "./-components/time-range-select";
import { TokenUsageChart } from "./-components/token-usage-chart";

type TimeRange = "7d" | "30d" | "90d";

export const Route = createFileRoute("/(app)/dashboard/analytics/usage/")({
	component: AnalyticsPage,
	head: ({ match }) =>
		noIndexSeo({
			title: `${m.analytics_title()} | StudyOS`,
			description: m.analytics_description(),
			pathname: match.pathname,
		}),
	beforeLoad: ({ context, matches, params }) => {
		const user = context.user;
		const pathname = matches
			.map((match) => match.pathname.slice(0, match.pathname.length - 1))
			.join("");

		if (!user || user.isAnonymous) {
			throw redirect({ to: "/sign-in", search: { from: pathname } });
		}
	},
});

function AnalyticsPage() {
	const [timeRange, setTimeRange] = useState<TimeRange>("30d");

	const { data: tokenUsage, isPending: isTokenPending } = useQuery(
		tokenUsageQueryOptions(timeRange),
	);
	const { data: conversationStats, isPending: isStatsPending } = useQuery(
		conversationStatsQueryOptions(timeRange),
	);
	const { data: modelSummary, isPending: isModelPending } = useQuery(
		modelSummaryQueryOptions(timeRange),
	);

	const isPending = isTokenPending || isStatsPending || isModelPending;

	return (
		<div className="space-y-6 p-6">
			<div className="flex items-center justify-between">
				<div>
					<h3 className="text-lg font-medium">{m.analytics_title()}</h3>
					<p className="text-sm text-muted-foreground">{m.analytics_description()}</p>
				</div>
				<TimeRangeSelect value={timeRange} onChange={setTimeRange} />
			</div>
			<Separator />

			<StatCards
				totals={conversationStats?.totals}
				modelSummary={modelSummary}
				isPending={isPending}
			/>

			<div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
				<TokenUsageChart data={tokenUsage} isPending={isTokenPending} />
				<ActivityChart data={conversationStats?.daily} isPending={isStatsPending} />
			</div>

			<CostBreakdown data={modelSummary} isPending={isModelPending} />
		</div>
	);
}
